/**
 * Client-side image-type and image-size guards for the template editors, the email footer and the composer.
 * All are early guards only — the server enforces the same allow-list (`ImageUploadTypes`) and each school's
 * own size limit — and on rejection the editor reports the failure, so no URL is inserted.
 *
 * PNG and JPEG only (SB-14897, SB-15889). Every upload goes through the blob validator, which requires
 * the content type to be in the server's `FileSettings:AllowedContentTypes`, and that lists only
 * `application/json`, `image/jpeg` and `image/png`. A wider list here would pass this guard and then
 * fail at storage. Widening it would mean changing configuration in three hosts, which is a separate
 * decision (owner, 2026-07-27). `image/jpg` is a non-standard spelling some browsers still emit.
 */

/**
 * What an organisation's own template editor accepts (SB-14897).
 */
export const ALLOWED_IMAGE_UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

/**
 * What the curated library accepts (SB-15889) — the same list.
 */
export const ALLOWED_LIBRARY_IMAGE_UPLOAD_TYPES = ALLOWED_IMAGE_UPLOAD_TYPES;

/**
 * The most any school accepts for an image, in megabytes (SB-17177). A school's `ImageUpload_FileSizeLimit_MB`
 * (DefaultConfigSetting 135) defaults to 10, and the settings screen refuses more
 * (`OrganisationSettingDirectory._maxImageUploadFileSizeLimitMB`), so this changes only with them.
 */
export const MAX_IMAGE_UPLOAD_SIZE_MB = 10;

/**
 * Whether an image is over {@link MAX_IMAGE_UPLOAD_SIZE_MB}, a megabyte being 1,000,000 bytes as the server
 * counts it (SB-17177). It is checked before the upload because the API host turns away a request over
 * 30,000,000 bytes itself, without a CORS header, so the browser could not read why. Below the cap the server
 * still checks the school's own limit, which may be lower.
 */
export const exceedsMaxImageUploadSize = (file: Blob): boolean => file.size > MAX_IMAGE_UPLOAD_SIZE_MB * 1000000;

/**
 * The values an image-upload refusal's text interpolates: the size limit, or, for a size limit that is not a
 * number (`SB_Organisation_Configuration_Setting_Is_Not_A_Number`), the setting and the school it is set for
 * (SB-17179).
 */
export interface ImageUploadErrorParams {
  maximumImageSizeMB?: number;
  configurationSetting?: string;
  organisationId?: number;
}

/**
 * Picks {@link ImageUploadErrorParams} out of the server's answer to a refused upload (SB-17179). Never pass the
 * answer itself to the translate pipe: a string body, such as a plain-text 400 or a host's own error page, makes
 * TranslatePipe throw while rendering. Destructuring a string, null or undefined just gives no values.
 */
export const imageUploadErrorParams = (body: ImageUploadErrorParams | null | undefined): ImageUploadErrorParams => {
  const { maximumImageSizeMB, configurationSetting, organisationId } = body ?? {};
  return { maximumImageSizeMB, configurationSetting, organisationId };
};

/**
 * An image upload refused: the label tag as its message, with the values the tag's text interpolates (SB-17178).
 * The values travel with the refusal, not in a field the next upload rewrites: TinyMCE hands over every image of a
 * paste in one go, and each refusal has to keep its own.
 */
export class ImageUploadRefusal extends Error {
  constructor(
    tag: string,
    readonly params: ImageUploadErrorParams = {}
  ) {
    super(tag);
  }
}
